import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Package, Plus, Search, AlertTriangle } from 'lucide-react';
import { detectarDuplicidade } from '@profitlens/dominio';
import { Cabecalho } from '../componentes/Cabecalho';
import { Carregando, Erro, Vazio } from '../componentes/Estados';
import { Reais, Texto } from '../componentes/Valor';
import { useAuth } from '../contextos/AuthContext';
import { useCategorias } from '../servicos/categorias';
import { useIndiceCodigos, useProdutos } from '../servicos/produtos';

export function Mercadorias() {
  const { produtos, carregando, erro } = useProdutos();
  const indice = useIndiceCodigos();
  const { podeEscrever } = useAuth();
  const { categorias } = useCategorias();
  const [busca, setBusca] = useState('');
  const [status, setStatus] = useState<'TODOS' | 'ATIVOS' | 'INATIVOS'>('ATIVOS');

  // A busca continua encontrando pelo nome da categoria, mesmo agora que o
  // produto guarda o id dela.
  const nomeCategoria = useMemo(
    () => Object.fromEntries(categorias.map((c) => [c.id, c.nome])) as Record<string, string>,
    [categorias],
  );

  const filtrados = useMemo(() => {
    const t = busca.trim().toLowerCase();
    return produtos.filter((p) => {
      if (status === 'ATIVOS' && !p.ativo) return false;
      if (status === 'INATIVOS' && p.ativo) return false;
      if (!t) return true;
      const codigos = (indice[p.id] ?? []).join(' ');
      return [p.nome, p.marca, p.modelo, nomeCategoria[p.categoriaId ?? ''], p.codigoInterno, codigos]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(t);
    });
  }, [produtos, indice, busca, status, nomeCategoria]);

  // Duplicidade calculada sobre o cadastro inteiro: o alerta aparece na lista,
  // não só na hora de salvar, senão duplicata antiga nunca é descoberta.
  const duplicados = useMemo(() => {
    const marcados = new Set<string>();
    for (const p of produtos) {
      const outros = produtos
        .filter((o) => o.id !== p.id)
        .map((o) => ({ id: o.id, nome: o.nome, marca: o.marca, codigos: indice[o.id] ?? [] }));
      if (detectarDuplicidade({ nome: p.nome, marca: p.marca, codigos: indice[p.id] ?? [] }, outros).length > 0) {
        marcados.add(p.id);
      }
    }
    return marcados;
  }, [produtos, indice]);

  if (carregando) return <Carregando />;
  if (erro) return <Erro titulo="Não foi possível carregar as mercadorias" detalhe={erro} />;

  return (
    <div className="flex flex-col gap-5">
      <Cabecalho
        titulo="Mercadorias"
        descricao="Os produtos que você cota e revende."
        acoes={
          podeEscrever && (
            <Link
              to="/mercadorias/nova"
              className="inline-flex items-center gap-1.5 rounded bg-marca px-3 py-1.5 text-sm font-semibold text-marca-tinta hover:opacity-90"
            >
              <Plus className="size-4" aria-hidden />
              Novo produto
            </Link>
          )
        }
      />

      <div className="flex flex-wrap gap-2">
        <label className="relative min-w-56 flex-1">
          <Search className="absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-tinta-3" aria-hidden />
          <input
            type="search"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            placeholder="Buscar por nome, marca, modelo ou código"
            className="w-full rounded border border-regua-2 bg-superficie py-1.5 pr-3 pl-8 text-sm outline-none focus:border-marca"
          />
        </label>
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value as typeof status)}
          className="rounded border border-regua-2 bg-superficie px-3 py-1.5 text-sm outline-none focus:border-marca"
        >
          <option value="ATIVOS">Ativos</option>
          <option value="INATIVOS">Inativos</option>
          <option value="TODOS">Todos</option>
        </select>
      </div>

      {filtrados.length === 0 ? (
        <Vazio
          icone={<Package className="size-8" />}
          titulo={produtos.length === 0 ? 'Nenhuma mercadoria cadastrada' : 'Nenhum resultado para este filtro'}
          descricao={
            produtos.length === 0
              ? 'O produto é o ponto de partida: é dele que saem a cotação com fornecedores e a busca de preço no mercado.'
              : 'Ajuste a busca ou troque o filtro de situação.'
          }
          acao={
            produtos.length === 0 &&
            podeEscrever && (
              <Link
                to="/mercadorias/nova"
                className="rounded bg-marca px-3 py-1.5 text-sm font-semibold text-marca-tinta hover:opacity-90"
              >
                Cadastrar a primeira
              </Link>
            )
          }
        />
      ) : (
        <>
          <div className="hidden overflow-x-auto rounded-md border border-regua-2 sm:block">
            <table className="w-full min-w-[46rem] border-collapse bg-superficie text-sm">
              <thead>
                <tr>
                  {['Produto', 'Marca', 'Código', 'Un', 'Preço-alvo', 'Situação'].map((h) => (
                    <th
                      key={h}
                      className="border-b border-regua-2 px-3 py-2 text-left font-mono text-[0.62rem] font-semibold tracking-wider text-marca uppercase"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtrados.map((p) => (
                  <tr key={p.id} className="border-b border-regua last:border-0 even:bg-marca/4">
                    <td className="px-3 py-2">
                      <Link to={`/mercadorias/${p.id}`} className="font-medium hover:text-marca">
                        {p.nome}
                      </Link>
                      {duplicados.has(p.id) && (
                        <span
                          className="ml-1.5 inline-flex items-center gap-1 text-[0.7rem] text-c-res"
                          title="Possível duplicidade com outro produto"
                        >
                          <AlertTriangle className="size-3" aria-hidden />
                          possível duplicata
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-tinta-2">
                      <Texto valor={p.marca} />
                    </td>
                    <td className="px-3 py-2 font-mono text-xs text-tinta-2">
                      {indice[p.id]?.[0] ?? <span className="text-tinta-3 italic">sem código</span>}
                    </td>
                    <td className="px-3 py-2 text-tinta-2">{p.unidadeMedida}</td>
                    <td className="px-3 py-2 text-right">
                      <Reais cents={p.precoAlvoCompraCents} />
                    </td>
                    <td className="px-3 py-2">
                      <span
                        className={`rounded-full border px-2 py-0.5 font-mono text-[0.6rem] font-semibold tracking-wider uppercase ${
                          p.ativo ? 'border-c-via text-c-via' : 'border-tinta-3 text-tinta-3'
                        }`}
                      >
                        {p.ativo ? 'Ativo' : 'Inativo'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <ul className="flex flex-col gap-2 sm:hidden">
            {filtrados.map((p) => (
              <li key={p.id} className="rounded-md border border-regua-2 bg-superficie p-3">
                <Link to={`/mercadorias/${p.id}`} className="flex flex-col gap-1">
                  <span className="font-medium">{p.nome}</span>
                  <span className="text-xs text-tinta-2">
                    <Texto valor={p.marca} /> · {p.unidadeMedida}
                  </span>
                  <span className="font-mono text-xs text-tinta-3">
                    {indice[p.id]?.[0] ?? 'sem código'}
                  </span>
                  <span className="mt-1 text-sm">
                    <Reais cents={p.precoAlvoCompraCents} />
                  </span>
                </Link>
              </li>
            ))}
          </ul>

          <p className="text-xs text-tinta-3">
            <span className="num">{filtrados.length}</span> de{' '}
            <span className="num">{produtos.length}</span>{' '}
            {produtos.length === 1 ? 'mercadoria' : 'mercadorias'}
          </p>
        </>
      )}
    </div>
  );
}
